import { useCallback, useEffect, useMemo, useState } from 'react';
import { generatePath } from 'react-router';

import { ExceptionsApi } from 'Api/Exceptions/ExceptionsApi';
import { IssuesApi } from 'Api/Issues/IssuesApi';
import { ClientUcrmApi } from 'Api/UCRM/UcrmApi';
import { UcrmDueDiligenceAssessmentConfigurationApi } from 'Api/UCRM/UcrmDueDiligenceAssessmentConfigurationApi';
import { Link } from 'Components/Buttons/Buttons';
import { FormFieldSelect } from 'Components/FormField/FormFieldSelect/FormFieldSelect';
import { PageLayoutDefault } from 'Components/PageLayout/PageLayoutDefault';
import { Placeholder } from 'Components/Placeholder/Placeholder';
import { Text } from 'Components/Text/Text';
import { DueDiligenceWorkflowDueDateModal } from 'Components/UcrmTprm/DueDiligenceWorkflowDueDateModal/DueDiligenceWorkflowDueDateModal';
import { IrqTargetCompletionDateModal } from 'Components/UcrmTprm/IrqTargetCompletionDateModal/IrqTargetCompletionDateModal';
import { SetActiveStatesModal } from 'Components/UcrmTprm/SetActiveStatesModal/SetActiveStatesModal';
import { UsersModal } from 'Components/UcrmTprm/UsersModal/UsersModal';
import { GENERIC_ERROR_MESSAGE } from 'Config/Errors';
import { ICON_ADD_CREATE } from 'Config/Icons';
import { UCRM_INTAKE } from 'Config/PathTemplates';
import { ASSESSMENTS_IN_PROGRESS, ASSESSMENTS_SUBMITTED, FINAL_REVIEW_IN_PROGRESS, IRQ_IN_PROGRESS, IRQ_NOT_FINALIZED, QUESTIONNAIRES_IN_PROGRESS, UCRM_BUSINESS_NOT_SUBMITTED_TO_BE_FINALIZED, UCRM_BUSINESS_PENDING_FINALIZATION, UCRM_QUESTIONNAIRES_SUBMITTED, UCRM_STEPS_FOR_NON_UCRM_USER, UCRM_STEPS_FOR_UCRM_USER, UCRM_USE_CASE_NOT_SUBMITTED_TO_BE_FINALIZED, UCRM_USE_CASE_PENDING_FINALIZATION, WORKFLOW_NOT_STARTED } from 'Config/Text';
import { UCRM_LISTING_ACTIVE_STATE_FILTER } from 'Config/Tooltips';
import { AuthHandler } from 'Helpers/Auth/AuthHandler';
import { loggedInUserHasFullAccessToUcrm } from 'Helpers/UserUtils';
import { DisplayedModal, OrganizationsAndAssessmentSubjectsTableRowData, UserTypeAndOrganizations } from 'Models/AssessmentSubject';
import { AssessmentWorkflowState, DueDiligenceWorkflowProgress } from 'Models/DueDiligenceWorkflow';
import { FolderVersion } from 'Models/Folders';
import { ActiveState, ActiveStateDisplayOption, FinalizationState, activeStateFilterOptions } from 'Models/Organization';
import { ProductModule } from 'Models/ProductModule';
import { BusinessResponseWithUseCases, BusinessWithUseCasesForNonUcrmUser, UcrmEntities, UseCase, UseCaseForNonUcrmUser } from 'Models/Ucrm';

import styles from './BusinessAndUseCaseListing.module.css';
import { BusinessAndUseCaseTable, BusinessAndUseCaseTableProps } from './BusinessAndUseCaseTable/BusinessAndUseCaseTable';
import { DeleteBusinessModal } from '../ManageBusiness/DeleteBusinessModal/DeleteBusinessModal';
import { DeleteUseCaseModal } from '../ManageUseCase/DeleteUseCaseModal/DeleteUseCaseModal';

export interface BusinessAndUseCaseListingProps {
    authHandler: AuthHandler;
    dueDiligenceAssessmentConfigurationApi: UcrmDueDiligenceAssessmentConfigurationApi;
    exceptionsApi: ExceptionsApi;
    issuesApi: IssuesApi;
    ucrmApi: ClientUcrmApi;
}

/**
 * The AI Governance (UCRM) "Businesses and Use Cases" page, which lists every business paired with each of its use cases (one table row per use case, or a single row for a business that has no use cases shown).
 * Users with full UCRM access see all businesses and their folders; other users get the reduced `...ForNonUcrmUser` shapes, no folders, and a longer progress step list (see `getDueDiligenceWorkflowProgressForUseCaseForNonUcrmUser`).
 * The active-state filter applies to both levels: a business without matching use cases is still shown on its own row if the business itself matches.
 * Row actions (deleting, setting active states, setting due dates) open modals from this page, and the modals that change data refetch the businesses on success.
 */
export const BusinessAndUseCaseListing = (props: BusinessAndUseCaseListingProps) => {
    const [activeStateDisplayOption, setActiveStateDisplayOption] = useState<ActiveStateDisplayOption>(ActiveStateDisplayOption.ACTIVE_ONLY);
    const [displayedModal, setDisplayedModal] = useState<DisplayedModal<UcrmEntities>>({ type: 'none' });
    const [userTypeAndBusinesses, setUserTypeAndBusinesses] = useState<UserTypeAndOrganizations<UcrmEntities>>();
    const [folders, setFolders] = useState<FolderVersion[] | null>();

    const [loadingErrorOccurred, setLoadingErrorOccurred] = useState(false);

    const getBusinesses = useCallback(async (): Promise<void> => {
        try {
            const userRoles = props.authHandler.getUserRoles();
            const userId = props.authHandler.getAuthenticatedUserSubject()!;
            const userHasFullUcrmAccess = loggedInUserHasFullAccessToUcrm(userRoles);

            if (userHasFullUcrmAccess) {
                const businesses = (await props.ucrmApi.getBusinesses()).data;
                setUserTypeAndBusinesses({ userHasFullAccess: true, userId, organizations: businesses });
            } else {
                const businesses = (await props.ucrmApi.getBusinessesForNonUcrmUser()).data;
                setUserTypeAndBusinesses({ userHasFullAccess: false, userId, organizations: businesses });
            }
        } catch (error) {
            console.error(error);
            setLoadingErrorOccurred(true);
        }
    }, [props.authHandler, props.ucrmApi]);

    const getFolders = useCallback(async (): Promise<void> => {
        try {
            const userRoles = props.authHandler.getUserRoles();
            const userHasFullUcrmAccess = loggedInUserHasFullAccessToUcrm(userRoles);

            if (userHasFullUcrmAccess) {
                const response = await props.ucrmApi.getAllCurrentFolders();
                setFolders(response.data);
            } else {
                setFolders(null);
            }
        } catch (error) {
            console.error(error);
            setLoadingErrorOccurred(true);
        }
    }, [props.authHandler, props.ucrmApi]);

    useEffect(() => {
        getBusinesses();
        getFolders();
    }, [getFolders, getBusinesses]);

    const useCasesById: Map<string, UseCase | UseCaseForNonUcrmUser> | undefined = useMemo(() => {
        if (!userTypeAndBusinesses) {
            return undefined;
        }

        return new Map(userTypeAndBusinesses.organizations.flatMap((business) => business.use_cases).map((useCase) => [useCase.id, useCase]));
    }, [userTypeAndBusinesses]);

    const foldersByBusinessId: Map<string, FolderVersion[]> | undefined = useMemo(() => {
        if (folders === undefined) {
            return undefined;
        }

        const _foldersByBusinessId = new Map<string, FolderVersion[]>();

        if (folders === null) {
            return _foldersByBusinessId;
        }

        folders.forEach((folder) => {
            const foldersForBusiness = _foldersByBusinessId.get(folder.third_party_id);
            if (foldersForBusiness) {
                foldersForBusiness.push(folder);
            } else {
                _foldersByBusinessId.set(folder.third_party_id, [folder]);
            }
        });

        return _foldersByBusinessId;
    }, [folders]);

    const tableRowsData: OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>[] | undefined = useMemo(() => {
        if (!(userTypeAndBusinesses && foldersByBusinessId !== undefined)) {
            return undefined;
        }

        const getCommonAssessmentsParentUseCase = (useCase: UseCase | UseCaseForNonUcrmUser): UseCase | UseCaseForNonUcrmUser | undefined => (useCase.assessment_workflow_setup.common_assessment_parent ? useCasesById?.get(useCase.assessment_workflow_setup.common_assessment_parent.id) : undefined);

        const userId = userTypeAndBusinesses.userId;
        const _dataForRows: OrganizationsAndAssessmentSubjectsTableRowData<UcrmEntities>[] = [];

        userTypeAndBusinesses.organizations.forEach((business) => {
            const businessMatchesActiveStateDisplayOption = (() => {
                switch (activeStateDisplayOption) {
                    case ActiveStateDisplayOption.ALL:
                        return true;
                    case ActiveStateDisplayOption.ACTIVE_ONLY:
                        return business.active_state === ActiveState.ACTIVE;
                    case ActiveStateDisplayOption.INACTIVE_ONLY:
                        return business.active_state === ActiveState.INACTIVE || business.use_cases.some((useCase) => useCase.active_state === ActiveState.INACTIVE);
                }
            })();

            const useCasesMatchingActiveStateDisplayOption = business.use_cases.filter((useCase) => {
                return (() => {
                    switch (activeStateDisplayOption) {
                        case ActiveStateDisplayOption.ALL:
                            return true;
                        case ActiveStateDisplayOption.ACTIVE_ONLY:
                            return useCase.active_state === ActiveState.ACTIVE;
                        case ActiveStateDisplayOption.INACTIVE_ONLY:
                            return useCase.active_state === ActiveState.INACTIVE;
                    }
                })();
            });

            if (useCasesMatchingActiveStateDisplayOption.length === 0) {
                if (businessMatchesActiveStateDisplayOption) {
                    if (userTypeAndBusinesses.userHasFullAccess) {
                        _dataForRows.push({ userHasFullAccess: true, userId, foldersByOrganizationId: foldersByBusinessId, organization: business as BusinessResponseWithUseCases });
                    } else {
                        _dataForRows.push({ userHasFullAccess: false, userId, organization: business as BusinessWithUseCasesForNonUcrmUser });
                    }
                }
            } else {
                useCasesMatchingActiveStateDisplayOption.forEach((useCase) => {
                    if (userTypeAndBusinesses.userHasFullAccess) {
                        const _business = business as BusinessResponseWithUseCases;
                        const _useCase = useCase as UseCase;
                        const assessmentSubjectData = {
                            assessmentSubject: _useCase,
                            dueDiligenceWorkflowProgress: getDueDiligenceWorkflowProgressForUseCase(_business, _useCase, getCommonAssessmentsParentUseCase),
                        };
                        _dataForRows.push({ userHasFullAccess: true, userId, foldersByOrganizationId: foldersByBusinessId, organization: _business, assessmentSubjectData });
                    } else {
                        const _business = business as BusinessWithUseCasesForNonUcrmUser;
                        const _useCase = useCase as UseCaseForNonUcrmUser;
                        const assessmentSubjectData = {
                            assessmentSubject: _useCase,
                            dueDiligenceWorkflowProgress: getDueDiligenceWorkflowProgressForUseCaseForNonUcrmUser(_business, _useCase, getCommonAssessmentsParentUseCase),
                        };
                        _dataForRows.push({ userHasFullAccess: false, userId, organization: _business, assessmentSubjectData });
                    }
                });
            }
        });

        return _dataForRows;
    }, [activeStateDisplayOption, useCasesById, userTypeAndBusinesses, foldersByBusinessId]);

    if (loadingErrorOccurred) {
        return <Text>{GENERIC_ERROR_MESSAGE}</Text>;
    }

    if (!(tableRowsData && userTypeAndBusinesses && foldersByBusinessId !== undefined)) {
        return <Placeholder />;
    }

    const hideModal = (): void => {
        setDisplayedModal({ type: 'none' });
    };

    const businessAndUseCaseTableProps: BusinessAndUseCaseTableProps = {
        onDisplayUsers: (userIds, header) => setDisplayedModal({ type: 'users', userIds, header }),
        onDeleteBusiness: (business) => setDisplayedModal({ type: 'deleteOrganization', organization: business }),
        onDeleteUseCase: (useCase) => setDisplayedModal({ type: 'deleteAssessmentSubject', assessmentSubject: useCase }),
        onSetActiveStates: (business) => setDisplayedModal({ type: 'setActiveStates', organization: business }),
        onSetAssessmentDueDate: (useCase) => setDisplayedModal({ type: 'setAssessmentDueDate', assessmentSubject: useCase }),
        onSetIrqTargetCompletionDate: (useCase) => setDisplayedModal({ type: 'setIrqTargetCompletionDate', assessmentSubject: useCase }),
        userTypeAndTableRowsData: { userHasFullUcrmAccess: userTypeAndBusinesses.userHasFullAccess, userId: userTypeAndBusinesses.userId, tableRowsData },
    };

    return (
        <>
            {displayedModal.type === 'users' && <UsersModal userIds={displayedModal.userIds} header={displayedModal.header} hideModal={hideModal} />}

            {displayedModal.type === 'setAssessmentDueDate' && <DueDiligenceWorkflowDueDateModal module={ProductModule.UCRM} dueDiligenceAssessmentConfigurationApi={props.dueDiligenceAssessmentConfigurationApi} assessmentSubject={displayedModal.assessmentSubject} hideModal={hideModal} moduleApi={props.ucrmApi} onDueDateSet={getBusinesses} />}
            {displayedModal.type === 'setIrqTargetCompletionDate' && <IrqTargetCompletionDateModal module={ProductModule.UCRM} hideModal={hideModal} assessmentSubject={displayedModal.assessmentSubject} targetDateSet={getBusinesses} moduleApi={props.ucrmApi} />}

            {displayedModal.type === 'deleteOrganization' && <DeleteBusinessModal exceptionsApi={props.exceptionsApi} issuesApi={props.issuesApi} onSuccess={getBusinesses} onHideModal={hideModal} business={displayedModal.organization} ucrmApi={props.ucrmApi} userHasFullUcrmAccess={userTypeAndBusinesses.userHasFullAccess} />}
            {displayedModal.type === 'deleteAssessmentSubject' && <DeleteUseCaseModal hideModal={hideModal} onSuccess={getBusinesses} useCase={displayedModal.assessmentSubject} ucrmApi={props.ucrmApi} userHasFullUcrmAccess={userTypeAndBusinesses.userHasFullAccess} />}

            {displayedModal.type === 'setActiveStates' && <SetActiveStatesModal organization={displayedModal.organization} module={ProductModule.UCRM} moduleApi={props.ucrmApi} hideModal={hideModal} onActiveStatesSubmitted={getBusinesses} />}

            <PageLayoutDefault
                headerTitle="Businesses and Use Cases"
                headerDescription="Businesses and use cases are paired into rows in the table below. Filters cause a row to be shown if their selections match either the business or the use case in the row."
                headerButtons={
                    <>
                        <Link variant="primaryButton" to={generatePath(UCRM_INTAKE)} fontAwesomeImage={ICON_ADD_CREATE}>
                            Create
                        </Link>
                    </>
                }
                body={[
                    {
                        content: (
                            <>
                                <div className={styles.activeStateFilter}>
                                    <FormFieldSelect tooltip={UCRM_LISTING_ACTIVE_STATE_FILTER} formFieldId="activeStateFilter" formFieldLabel="Show active/inactive" options={activeStateFilterOptions} selectedOption={activeStateDisplayOption} handleChange={(value) => setActiveStateDisplayOption(value as ActiveStateDisplayOption)} notClearable />
                                </div>
                                <BusinessAndUseCaseTable {...businessAndUseCaseTableProps} />
                            </>
                        ),
                    },
                ]}
            />
        </>
    );
};

/**
 * Builds the progress shown in the "Assessment Progress" column for a use case, as seen by a user with full UCRM access.
 * `steps` is a copy of `UCRM_STEPS_FOR_UCRM_USER`, and `completedStepNumber` is the index of the latest completed step (the cell marks every step at or below it as complete):
 * 0 business and use case created, 1 business finalized, 2 use case finalized, 3 workflow started, 4 IRQ submitted, 5 questionnaires submitted, 6 assessments submitted (final review in progress).
 * The checks run from the latest stage back to the earliest, and the first match wins.
 * A common-assessment child (a use case whose `assessment_workflow_setup.common_assessment_parent` is set) uses its parent's questionnaires and assessments, so steps 5 and 6 and the in-progress text name the parent, and the questionnaire/assessment completion flags are read from the parent.
 * The child's own workflow state still decides which branch applies.
 * If the parent is not among the loaded use cases, the child is treated as if it had no parent.
 * @param business The business that owns `useCase`.
 * @param useCase The use case whose progress is built.
 * @param getCommonAssessmentsParentUseCase Looks up the use case that provides `useCase`'s shared assessments, or returns `undefined` if there is none.
 */
export const getDueDiligenceWorkflowProgressForUseCase = (business: BusinessResponseWithUseCases, useCase: UseCase, getCommonAssessmentsParentUseCase: (useCase: UseCase | UseCaseForNonUcrmUser) => UseCase | UseCaseForNonUcrmUser | undefined): DueDiligenceWorkflowProgress => {
    const steps = [...UCRM_STEPS_FOR_UCRM_USER];
    const commonAssessmentsParentUseCase = getCommonAssessmentsParentUseCase(useCase);
    const useCaseWithAssessments = commonAssessmentsParentUseCase ?? useCase;

    if (commonAssessmentsParentUseCase) {
        steps[5] = `${UCRM_QUESTIONNAIRES_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})`;
        steps[6] = `${ASSESSMENTS_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})`;
    }

    if (useCase.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE) {
        if (useCaseWithAssessments.assessment_workflow_data.all_assessments_completed) {
            return {
                completedStepNumber: 6,
                inProgressStepDescription: FINAL_REVIEW_IN_PROGRESS,
                steps,
            };
        } else if (useCaseWithAssessments.assessment_workflow_data.all_questionnaires_completed) {
            return {
                completedStepNumber: 5,
                inProgressStepDescription: commonAssessmentsParentUseCase ? `${ASSESSMENTS_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})` : ASSESSMENTS_IN_PROGRESS,
                steps,
            };
        } else {
            return {
                completedStepNumber: 4,
                inProgressStepDescription: commonAssessmentsParentUseCase ? `${QUESTIONNAIRES_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})` : QUESTIONNAIRES_IN_PROGRESS,
                steps,
            };
        }
    } else if (useCase.assessment_workflow_data.state === AssessmentWorkflowState.EVALUATING_INHERENT_RISK) {
        return {
            completedStepNumber: 3,
            inProgressStepDescription: IRQ_IN_PROGRESS,
            steps,
        };
    } else if (useCase.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 2,
            inProgressStepDescription: WORKFLOW_NOT_STARTED,
            steps,
        };
    } else if (business.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 1,
            inProgressStepDescription: UCRM_USE_CASE_PENDING_FINALIZATION,
            steps,
        };
    } else {
        return {
            completedStepNumber: 0,
            inProgressStepDescription: UCRM_BUSINESS_PENDING_FINALIZATION,
            steps,
        };
    }
};

/**
 * The counterpart of `getDueDiligenceWorkflowProgressForUseCase` for a user without full UCRM access, who also sees the steps for submitting drafts to be finalized.
 * `steps` is a copy of `UCRM_STEPS_FOR_NON_UCRM_USER`, which inserts two steps at indexes 1 and 2, so every later step is offset by 2 from the full-access list:
 * 0 business and use case created, 1 business submitted to be finalized, 2 use case submitted to be finalized, 3 business finalized, 4 use case finalized, 5 workflow started, 6 IRQ finalized, 7 questionnaires submitted, 8 assessments submitted (final review in progress).
 * Common-assessment children are handled the same way as in `getDueDiligenceWorkflowProgressForUseCase`, with the parent named in steps 7 and 8 instead of 5 and 6.
 * Because the first matching check wins, a finalized business reports step 3 as completed even if its use case has not been submitted to be finalized yet.
 * @param business The business that owns `useCase`.
 * @param useCase The use case whose progress is built.
 * @param getCommonAssessmentsParentUseCase Looks up the use case that provides `useCase`'s shared assessments, or returns `undefined` if there is none.
 */
export const getDueDiligenceWorkflowProgressForUseCaseForNonUcrmUser = (business: BusinessWithUseCasesForNonUcrmUser, useCase: UseCaseForNonUcrmUser, getCommonAssessmentsParentUseCase: (useCase: UseCase | UseCaseForNonUcrmUser) => UseCase | UseCaseForNonUcrmUser | undefined): DueDiligenceWorkflowProgress => {
    const steps = [...UCRM_STEPS_FOR_NON_UCRM_USER];
    const commonAssessmentsParentUseCase = getCommonAssessmentsParentUseCase(useCase);
    const useCaseWithAssessments = commonAssessmentsParentUseCase ?? useCase;

    if (commonAssessmentsParentUseCase) {
        steps[7] = `${UCRM_QUESTIONNAIRES_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})`;
        steps[8] = `${ASSESSMENTS_SUBMITTED} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})`;
    }

    if (useCase.assessment_workflow_data.state === AssessmentWorkflowState.PERFORMING_DUE_DILIGENCE) {
        if (useCaseWithAssessments.assessment_workflow_data.all_assessments_completed) {
            return {
                completedStepNumber: 8,
                inProgressStepDescription: FINAL_REVIEW_IN_PROGRESS,
                steps,
            };
        } else if (useCaseWithAssessments.assessment_workflow_data.all_questionnaires_completed) {
            return {
                completedStepNumber: 7,
                inProgressStepDescription: commonAssessmentsParentUseCase ? `${ASSESSMENTS_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})` : ASSESSMENTS_IN_PROGRESS,
                steps,
            };
        } else {
            return {
                completedStepNumber: 6,
                inProgressStepDescription: commonAssessmentsParentUseCase ? `${QUESTIONNAIRES_IN_PROGRESS} (shared due diligence assessments provided by ${commonAssessmentsParentUseCase.name})` : QUESTIONNAIRES_IN_PROGRESS,
                steps,
            };
        }
    } else if (useCase.assessment_workflow_data.state === AssessmentWorkflowState.EVALUATING_INHERENT_RISK) {
        return {
            completedStepNumber: 5,
            inProgressStepDescription: IRQ_NOT_FINALIZED,
            steps,
        };
    } else if (useCase.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 4,
            inProgressStepDescription: WORKFLOW_NOT_STARTED,
            steps,
        };
    } else if (business.finalization_state === FinalizationState.FINALIZED) {
        return {
            completedStepNumber: 3,
            inProgressStepDescription: UCRM_USE_CASE_PENDING_FINALIZATION,
            steps,
        };
    } else if (useCase.finalization_state === FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED) {
        return {
            completedStepNumber: 2,
            inProgressStepDescription: UCRM_BUSINESS_PENDING_FINALIZATION,
            steps,
        };
    } else if (business.finalization_state === FinalizationState.DRAFT_SUBMITTED_TO_BE_FINALIZED) {
        return {
            completedStepNumber: 1,
            inProgressStepDescription: UCRM_USE_CASE_NOT_SUBMITTED_TO_BE_FINALIZED,
            steps,
        };
    } else {
        return {
            completedStepNumber: 0,
            inProgressStepDescription: UCRM_BUSINESS_NOT_SUBMITTED_TO_BE_FINALIZED,
            steps,
        };
    }
};
